import { defineStore } from 'pinia'
import { ref } from 'vue'
import FlexSearch from 'flexsearch'
import * as repo from '@/db/repositories'
import { createId } from '@/utils/id'
import { extractBookText } from '@/epub/extract-text'
import { chunkText } from '@/search/chunk'
import { makeSnippet } from '@/search/snippet'
import type { Book, Highlight } from '@/types/domain'
import type { BookIndexChunk, SearchHit } from '@/types/search'

/**
 * Store de búsqueda global.
 *
 * Dos capas (ver `types/search.ts` y el PRD del Área B):
 *   1. **Persistente (IndexedDB, tabla `bookIndex`)**: chunks de texto de los
 *      EPUBs y de las anotaciones. Fuente canónica.
 *   2. **En memoria (FlexSearch.Index)**: mapea `chunkId → texto`,
 *      reconstruido al arrancar leyendo los chunks. Aporta matching difuso
 *      (#7: sin acentos, segmentado) de forma nativa.
 *
 * El texto de los libros se extrae al importar (`indexBook`) y la migración
 * de libros antiguos corre en background en `init()`.
 */

export const useSearchStore = defineStore('search', () => {
  /**
   * Índice FlexSearch: chunkId → texto.
   * `forward` (tokeniza por prefijos) + `normalize` (quita acentos) dan
   * matching difuso (#7). Los tipos de @types/flexsearch no incluyen
   * `normalize`, así que se castean las opciones.
   */
  const index = new FlexSearch.Index({
    tokenize: 'forward',
    cache: false,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } as any)

  /** Mapa chunkId → BookIndexChunk (para resolver metadatos en resultados). */
  const chunksById = new Map<string, BookIndexChunk>()
  /**
   * Set de `${bookId}:${contentHash}` ya indexados. Evita el escaneo O(n)
   * sobre `chunksById` que hacía `indexBook` en cada libro (con la migración
   * de una biblioteca grande eso era O(libros × chunks)).
   */
  const indexedBooks = new Set<string>()

  const ready = ref(false)
  const migrating = ref(false)
  const migrationTotal = ref(0)
  const migrationDone = ref(0)

  /**
   * Inicializa el índice: carga todos los chunks de IDB, los añade a
   * FlexSearch y, si hay libros sin indexar, los procesa en background.
   */
  async function init() {
    if (ready.value) return
    const all = await repo.getAllIndexChunks()
    for (const chunk of all) {
      chunksById.set(chunk.id, chunk)
      if (chunk.kind === 'book' && chunk.contentHash) {
        indexedBooks.add(`${chunk.bookId}:${chunk.contentHash}`)
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ;(index as any).add(chunk.id, chunk.text)
    }
    ready.value = true
    // Migración de libros antiguos sin indexar (no bloquea ready).
    void migrateUnindexed()
  }

  /** Procesa en background los libros que aún no tienen chunks en el índice. */
  async function migrateUnindexed() {
    try {
      const books = await repo.getAllBooks()
      // Un libro está indexado si ya tiene algún chunk 'book' con su contentHash.
      const pending = books.filter(
        (b) => !(b.contentHash && indexedBooks.has(`${b.id}:${b.contentHash}`)),
      )
      if (pending.length === 0) return

      migrating.value = true
      migrationTotal.value = pending.length
      migrationDone.value = 0
      for (const book of pending) {
        try {
          // v18: el binario ya no vive inline en el libro; pedirlo bajo demanda.
          const blob = book.fileBlob ?? (await repo.getBookFile(book.id))
          if (!blob) {
            console.warn(`[search] no se encontró el archivo de "${book.title}"`)
            continue
          }
          await indexBook(book, blob)
        } catch (e) {
          // Un libro roto no debe abortar la migración del resto.
          console.warn(`[search] no se pudo indexar "${book.title}":`, e)
        }
        migrationDone.value++
        // Ceder al event loop para no congelar la UI.
        await new Promise((r) => setTimeout(r, 0))
      }
    } finally {
      migrating.value = false
    }
  }

  /**
   * Indexa el texto de un libro EPUB. Idempotente por `contentHash`: si ya hay
   * chunks 'book' para ese hash, no hace nada.
   */
  async function indexBook(book: Book, blob: Blob): Promise<void> {
    // Nota: NO se exige `ready`. El índice en memoria se construye perezosamente
    // (al abrir la búsqueda), pero un libro recién importado debe persistir sus
    // chunks igualmente; si no, nunca sería buscable.
    // Idempotencia por contentHash.
    if (book.contentHash && indexedBooks.has(`${book.id}:${book.contentHash}`)) return

    // Limpiar chunks previos de este libro (p. ej. reimport con otro hash).
    await removeBookFromIndex(book.id)

    let chapters
    try {
      chapters = await extractBookText(blob)
    } catch (e) {
      console.warn(`[search] extracción de texto falló para "${book.title}":`, e)
      return
    }

    const chunks: BookIndexChunk[] = []
    for (const chapter of chapters) {
      const pieces = chunkText(chapter.text)
      for (const piece of pieces) {
        const id = createId()
        const chunk: BookIndexChunk = {
          id,
          bookId: book.id,
          contentHash: book.contentHash ?? null,
          chunkIndex: piece.index,
          kind: 'book',
          chapterHref: chapter.chapterHref,
          spineIndex: chapter.spineIndex,
          cfi: null,
          text: piece.text,
        }
        chunks.push(chunk)
        chunksById.set(id, chunk)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ;(index as any).add(id, piece.text)
      }
    }

    if (chunks.length > 0) {
      await repo.bulkPutIndexChunks(chunks)
      if (book.contentHash) indexedBooks.add(`${book.id}:${book.contentHash}`)
    }
  }

  /**
   * Indexa (o reindexa) las anotaciones de un libro. Llamar tras crear/editar/
   * borrar highlights. Reescribe todos los chunks `kind:'highlight'` del libro.
   */
  async function reindexHighlights(bookId: string, highlights: Highlight[]): Promise<void> {
    // Sin exigir `ready` (índice perezoso): los chunks deben persistir igual.
    // Borrar chunks de highlight existentes (memoria + IDB).
    const toRemove: string[] = []
    for (const [id, chunk] of chunksById) {
      if (chunk.bookId === bookId && chunk.kind === 'highlight') {
        toRemove.push(id)
      }
    }
    for (const id of toRemove) {
      chunksById.delete(id)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ;(index as any).remove(id)
    }
    if (toRemove.length > 0) await repo.deleteHighlightChunksForBook(bookId)

    // Insertar los actuales.
    const chunks: BookIndexChunk[] = []
    for (const h of highlights) {
      const text = [h.text, h.note].filter(Boolean).join(' — ').trim()
      if (text.length < 2) continue
      const id = createId()
      const chunk: BookIndexChunk = {
        id,
        bookId,
        contentHash: null,
        chunkIndex: 0,
        kind: 'highlight',
        chapterHref: null,
        spineIndex: null,
        cfi: h.cfi,
        text,
      }
      chunks.push(chunk)
      chunksById.set(id, chunk)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ;(index as any).add(id, text)
    }
    if (chunks.length > 0) await repo.bulkPutIndexChunks(chunks)
  }

  /** Borra del índice (memoria + IDB) todos los chunks de un libro. */
  async function removeBookFromIndex(bookId: string): Promise<void> {
    const toRemove: string[] = []
    for (const [id, chunk] of chunksById) {
      if (chunk.bookId === bookId) toRemove.push(id)
    }
    for (const key of indexedBooks) {
      if (key.startsWith(`${bookId}:`)) indexedBooks.delete(key)
    }
    for (const id of toRemove) {
      chunksById.delete(id)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ;(index as any).remove(id)
    }
    await repo.deleteIndexChunksForBook(bookId)
  }

  /**
   * Ejecuta una búsqueda global. Devuelve hits con snippet y metadatos.
   * Usa FlexSearch (matching difuso, sin acentos — #7).
   */
  async function search(query: string, limit = 50): Promise<SearchHit[]> {
    const q = query.trim()
    if (q.length < 2 || !ready.value) return []

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const result = await (index as any).search(q, { limit })
    // FlexSearch devuelve `number[]` (ids) o `{ id, score }[]` según options.
    const rawHits = Array.isArray(result) ? result : []
    const hits: SearchHit[] = []
    for (const r of rawHits) {
      const id = typeof r === 'number' || typeof r === 'string' ? r : r.id
      const score = typeof r === 'object' && r !== null ? (r.score ?? 0) : 1
      const chunk = chunksById.get(String(id))
      if (!chunk) continue
      hits.push({
        chunkId: chunk.id,
        bookId: chunk.bookId,
        kind: chunk.kind,
        chapterHref: chunk.chapterHref,
        cfi: chunk.cfi,
        snippet: makeSnippet(chunk.text, q),
        score,
      })
    }
    // Ordenar por score descendente (los más relevantes primero).
    hits.sort((a, b) => b.score - a.score)
    return hits
  }

  return {
    ready,
    migrating,
    migrationTotal,
    migrationDone,
    init,
    indexBook,
    reindexHighlights,
    removeBookFromIndex,
    search,
  }
})
